// Digest: "what changed" across a period, grouped into themes.
//
// Built deterministically from published stories at publish time and stored as
// ONE public document (meta/digest). Headlines and paragraphs are ASSEMBLED
// from titles, flags, counts, and prepared summaries; nothing is generated.
//
// AI hook: a theme can carry an AI-written headline/narrative supplied as an
// override (written offline by a trusted script). An override is applied only
// if its sourcesHash still matches the theme's current sources, so stale AI
// text never describes a changed set of stories.
import { TOPIC_LABELS, type ContentType, type Story } from "./model.ts";
import { effectiveFlag, type FlagStatus } from "./flags.ts";
import { PRODUCT_BY_ID } from "./products.ts";
import { plainSummary } from "./summaryText.ts";

export const DIGEST_PERIODS = ["today", "week", "month"] as const;
export type DigestPeriod = (typeof DIGEST_PERIODS)[number];
export const PERIOD_LABELS: Record<DigestPeriod, string> = { today: "Today", week: "This week", month: "This month" };
export const PERIOD_DAYS: Record<DigestPeriod, number> = { today: 1, week: 7, month: 30 };
export const THEME_LIMIT: Record<DigestPeriod, number> = { today: 4, week: 5, month: 6 };
export const SOURCES_PER_THEME = 6;

export type NarrativeProvenance = "assembled" | "ai_assisted";

export interface DigestSource {
  storyId: string;
  title: string;
  contentType: ContentType;
  sourceName: string;
  date: number;
  flag: FlagStatus | null;
  isSample: boolean;
  unreviewed: boolean;
  /** Same title on the same day on other release-note pages (shown once). */
  alsoOn: number;
}

export interface DigestTheme {
  id: string; // product id, or "topic:<topic>"
  productId: string | null;
  name: string;
  headline: string;
  narrative: string;
  provenance: NarrativeProvenance;
  model: string | null;
  count: number;
  flagCounts: Partial<Record<FlagStatus, number>>;
  sources: DigestSource[];
  sourcesHash: string;
}

export interface DigestPeriodData {
  period: DigestPeriod;
  from: number;
  to: number;
  total: number;
  overview: string;
  themes: DigestTheme[];
  others: { id: string; name: string; productId: string | null; count: number }[];
}

export interface DigestDoc {
  generatedAt: number;
  periods: Record<DigestPeriod, DigestPeriodData>;
}

export interface NarrativeOverride {
  headline: string;
  narrative: string;
  model: string;
  sourcesHash: string;
}

/** Small deterministic string hash (FNV-1a), same result in Node and browsers. */
export function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

export function firstSentence(text: string, max = 240): string {
  const t = text.replace(/\s+/g, " ").trim();
  const m = t.match(/^(.+?[.!?])(\s|$)/);
  const s = m ? m[1] : t;
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ")).trim()}…`;
}

const FLAG_WEIGHT: Record<FlagStatus, number> = { ga: 3, retired: 2.5, deprecated: 2.5, public_preview: 2, private_preview: 1.5, beta: 2 };
const FLAG_PHRASE: Record<FlagStatus, (n: number) => string> = {
  ga: (n) => `${n} now GA`,
  public_preview: (n) => `${n} in Public Preview`,
  private_preview: (n) => `${n} in Private Preview`,
  beta: (n) => `${n} in Beta`,
  deprecated: (n) => `${n} deprecating`,
  retired: (n) => `${n} retired`,
};
const FLAG_ORDER: FlagStatus[] = ["ga", "public_preview", "private_preview", "beta", "deprecated", "retired"];

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
function joinList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
}
const quote = (t: string) => `“${t.replace(/[.\s]+$/, "")}”`;

function themeKey(s: Story): { id: string; productId: string | null; name: string } {
  const pid = s.products.find((p) => PRODUCT_BY_ID.has(p));
  if (pid) return { id: pid, productId: pid, name: PRODUCT_BY_ID.get(pid)!.name };
  return { id: `topic:${s.primaryTopic}`, productId: null, name: TOPIC_LABELS[s.primaryTopic] };
}

const normTitle = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const dayOf = (s: Story) => new Date(s.publishedAt ?? s.sortAt).toISOString().slice(0, 10);

/**
 * Display-level collapse: the docs sometimes publish one entry on two pages
 * (e.g. product + feature release notes). Same normalized title on the same day
 * is shown once; stored stories are untouched.
 */
export function collapseRepeats(stories: Story[]): { story: Story; alsoOn: number }[] {
  const byKey = new Map<string, { story: Story; alsoOn: number }>();
  for (const s of [...stories].sort((a, b) => b.sortAt - a.sortAt || a.id.localeCompare(b.id))) {
    const k = `${normTitle(s.title)}|${dayOf(s)}`;
    const hit = byKey.get(k);
    if (hit) hit.alsoOn++;
    else byKey.set(k, { story: s, alsoOn: 0 });
  }
  return [...byKey.values()];
}

function buildTheme(key: { id: string; productId: string | null; name: string }, entries: { story: Story; alsoOn: number }[]): DigestTheme {
  const stories = entries.map((e) => e.story);
  const alsoOn = new Map(entries.map((e) => [e.story.id, e.alsoOn]));
  const flagged = stories.map((s) => ({ s, f: effectiveFlag(s)?.status ?? null }));
  const flagCounts: Partial<Record<FlagStatus, number>> = {};
  for (const { f } of flagged) if (f) flagCounts[f] = (flagCounts[f] ?? 0) + 1;
  // Lead with status changes (GA, retirements…), then newest.
  const ordered = [...flagged].sort(
    (a, b) => (b.f ? FLAG_WEIGHT[b.f] : 0) - (a.f ? FLAG_WEIGHT[a.f] : 0) || b.s.sortAt - a.s.sortAt || a.s.id.localeCompare(b.s.id),
  );
  const sources: DigestSource[] = ordered.slice(0, SOURCES_PER_THEME).map(({ s, f }) => ({
    storyId: s.id,
    title: s.title,
    contentType: s.contentType,
    sourceName: s.sourceName,
    date: s.publishedAt ?? s.sortAt,
    flag: f,
    isSample: s.isSample,
    unreviewed: s.summaryProvenance === "source_excerpt",
    alsoOn: alsoOn.get(s.id) ?? 0,
  }));

  const flagSummary = FLAG_ORDER.filter((f) => flagCounts[f]).map((f) => FLAG_PHRASE[f](flagCounts[f]!));
  const headline = `${key.name}: ${plural(stories.length, "update")}${flagSummary.length ? ` · ${flagSummary.join(", ")}` : ""}`;

  const lead = ordered[0].s;
  const leadText = firstSentence(plainSummary(lead.summaries.plainEnglish) || lead.title);
  const also = ordered.slice(1, 4).map(({ s }) => quote(s.title));
  const rest = stories.length - 1 - also.length;
  const narrative = [
    `${quote(lead.title)}: ${leadText}`,
    also.length ? `Also ${also.length === 1 ? "notable" : "in this theme"}: ${joinList(also)}.` : "",
    rest > 0 ? `${plural(rest, "more update")} below or on the Map.` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return {
    id: key.id,
    productId: key.productId,
    name: key.name,
    headline,
    narrative,
    provenance: "assembled",
    model: null,
    count: stories.length,
    flagCounts,
    sources,
    sourcesHash: fnv1a(stories.map((s) => s.id).sort().join(",")),
  };
}

function buildPeriod(period: DigestPeriod, published: Story[], now: number, overrides: Map<string, NarrativeOverride>): DigestPeriodData {
  const from = now - PERIOD_DAYS[period] * 86_400_000;
  const inWindow = collapseRepeats(
    published.filter((s) => {
      const t = s.publishedAt ?? s.sortAt;
      return t >= from && t <= now + 86_400_000;
    }),
  );
  const groups = new Map<string, { key: ReturnType<typeof themeKey>; entries: { story: Story; alsoOn: number }[] }>();
  for (const e of inWindow) {
    const key = themeKey(e.story);
    if (!groups.has(key.id)) groups.set(key.id, { key, entries: [] });
    groups.get(key.id)!.entries.push(e);
  }
  const themes = [...groups.values()].map((g) => buildTheme(g.key, g.entries));
  const weight = (t: DigestTheme) => t.count + Object.entries(t.flagCounts).reduce((n, [f, c]) => n + FLAG_WEIGHT[f as FlagStatus] * (c ?? 0) * 0.5, 0);
  themes.sort((a, b) => weight(b) - weight(a) || b.count - a.count || a.id.localeCompare(b.id));

  const top = themes.slice(0, THEME_LIMIT[period]).map((t) => {
    const o = overrides.get(`${period}:${t.id}`);
    return o && o.sourcesHash === t.sourcesHash
      ? { ...t, headline: o.headline, narrative: o.narrative, provenance: "ai_assisted" as const, model: o.model }
      : t;
  });
  const others = themes.slice(THEME_LIMIT[period]).map((t) => ({ id: t.id, name: t.name, productId: t.productId, count: t.count }));

  const totals: Partial<Record<FlagStatus, number>> = {};
  for (const t of themes) for (const [f, c] of Object.entries(t.flagCounts)) totals[f as FlagStatus] = (totals[f as FlagStatus] ?? 0) + (c ?? 0);
  // "Most active" means most updates (theme order also weighs status changes).
  const busiest = [...themes].sort((a, b) => b.count - a.count || a.id.localeCompare(b.id)).slice(0, 3).map((t) => `${t.name} (${t.count})`);
  const statusBits = [
    totals.ga ? `${totals.ga} reached GA` : "",
    (totals.public_preview ?? 0) + (totals.private_preview ?? 0) + (totals.beta ?? 0)
      ? `${(totals.public_preview ?? 0) + (totals.private_preview ?? 0) + (totals.beta ?? 0)} entered Beta or Preview`
      : "",
    (totals.deprecated ?? 0) + (totals.retired ?? 0) ? `${(totals.deprecated ?? 0) + (totals.retired ?? 0)} are deprecating or retired` : "",
  ].filter(Boolean);
  const overview = inWindow.length
    ? [
        `${plural(inWindow.length, "update")} across ${plural(themes.length, "product area")}.`,
        busiest.length ? `Most active: ${joinList(busiest)}.` : "",
        statusBits.length ? `${joinList(statusBits)[0].toUpperCase()}${joinList(statusBits).slice(1)}.` : "",
      ].filter(Boolean).join(" ")
    : "No published updates in this period yet.";

  return { period, from, to: now, total: inWindow.length, overview, themes: top, others };
}

export function buildDigest(published: Story[], now = Date.now(), overrides = new Map<string, NarrativeOverride>()): DigestDoc {
  const periods = {} as Record<DigestPeriod, DigestPeriodData>;
  for (const p of DIGEST_PERIODS) periods[p] = buildPeriod(p, published, now, overrides);
  return { generatedAt: now, periods };
}
